// Cumulative Exposure Decision Tree — shared content

export const DT_INTRO =
  "Use this decision tree when deciding whether a material, product, system, routine, environment, or daily-use item deserves attention.\n\nCumulative exposure does not come from the home alone. It can come from the spaces people occupy, the products they use, the air they breathe, the water they drink, the surfaces they touch, the foods they prepare, the clothing they wear, the cosmetics and personal-care products they apply, and the environments they move through each day.\n\nThe goal is not to eliminate every possible exposure. The goal is to identify the inputs that are repeated, close to the body, high-contact, long-lasting, difficult to clean, moisture-related, or practical to reduce.";

export const DT_DISCLAIMER =
  "This tool is for education and decision support. It does not diagnose health conditions or determine whether a product or environment is safe or unsafe for a specific person.";

export const DT_CALLOUT =
  "Not every exposure deserves the same response. Use the Cumulative Exposure Decision Tree to identify which sources are repeated, close, high-contact, moisture-related, or practical to reduce.";

export const DT_APPLIES_TO = [
  "Building materials", "Furniture", "Flooring", "Paint and finishes",
  "Cleaning products", "Fragrance products", "Cosmetics", "Skin-care products",
  "Hair-care products", "Oral-care products", "Laundry products",
  "Clothing and textiles", "Cookware", "Food-storage materials", "Drinking water",
  "Workplace exposures", "Vehicle interiors", "Hobbies and craft materials",
  "Fitness, salon, spa, or wellness environments", "Public or shared spaces",
  "Renovation, construction, or maintenance activities",
];

// Each node: question, and the two branches. `next` continues the flow.
export const DT_NODES = [
  {
    q: "Source or input identified?",
    no: { label: "Monitor", outcome: "E" },
    yes: { label: "Continue", next: true },
  },
  {
    q: "Repeated, large, close, long-lasting, or used directly on the body?",
    no: { label: "Lower priority — or handle if easy", outcome: "C" },
    yes: { label: "Continue", next: true },
  },
  {
    q: "Clear exposure pathway?",
    no: { label: "Document and monitor", outcome: "E" },
    yes: { label: "Continue", next: true },
  },
  {
    q: "Near high-use, sleeping, child, work, kitchen, bathroom, vehicle, or body-contact use?",
    no: { label: "Moderate or lower priority", outcome: "C" },
    yes: { label: "Continue", next: true },
  },
  {
    q: "Moisture, heat, combustion, spraying, dust, or disturbance involved?",
    yes: { label: "Move higher in priority — reduce the pathway", outcome: "A" },
    no: { label: "Continue", next: true },
  },
  {
    q: "Easy to reduce or remove?",
    yes: { label: "Reduce, remove, clean, ventilate, replace, simplify, or maintain", outcome: "A" },
    no: { label: "Continue", next: true },
  },
  {
    q: "Connected to a planned purchase, project, or routine change?",
    yes: { label: "Address during that decision window", outcome: "D" },
    no: { label: "Continue", next: true },
  },
  {
    q: "Would testing, documentation, or professional evaluation change a decision?",
    yes: { label: "Test, document, or evaluate before remedy", outcome: "B" },
    no: { label: "Defer with a plan — or monitor", outcome: "D" },
  },
];

export const DT_OUTCOMES = [
  {
    key: "A", title: "Act Now",
    when: [
      "Moisture is active or unresolved.",
      "The source is strong, repeated, and close to occupants.",
      "The product is applied directly to the body every day.",
      "The source is in a high-use, sleeping, child, work, kitchen, bathroom, or vehicle environment.",
      "The correction is practical and meaningful.",
      "Delaying may create more damage, rework, or continued unnecessary input.",
    ],
    examples: [
      "Correct active leaks.",
      "Remove water-damaged porous materials where appropriate.",
      "Repair bathroom exhaust that does not discharge outdoors.",
      "Replace HVAC filters after dusty renovation work.",
      "Stop using unnecessary fragrance products dispersed into indoor air.",
      "Simplify a crowded personal-care routine.",
      "Replace a daily-use product that is irritating, heavily fragranced, or poorly documented when a simpler option is practical.",
    ],
  },
  {
    key: "B", title: "Test, Document, or Evaluate First",
    when: [
      "The source is uncertain.",
      "The condition may affect a major purchase, renovation, or replacement decision.",
      "The wrong action could waste money or create rework.",
      "Product documentation, testing, or qualified professional evaluation would change the plan.",
    ],
    examples: [
      "Water testing before filtration.",
      "Radon testing before mitigation.",
      "Lead or asbestos evaluation before disturbing older materials.",
      "Moisture assessment before finishing a basement.",
      "HVAC evaluation before replacing equipment.",
      "Product documentation review before selecting large-surface materials.",
      "Certification or ingredient review before selecting high-contact textiles, mattresses, cosmetics, or cleaning products.",
    ],
  },
  {
    key: "C", title: "Reduce and Maintain",
    when: [
      "The source cannot be fully removed now.",
      "Lower-cost steps can reduce the burden.",
      "Maintenance affects performance.",
      "The exposure comes from routine use rather than one major source.",
    ],
    examples: [
      "Clean dust reservoirs.",
      "Use exhaust fans consistently.",
      "Wash bedding and textiles.",
      "Maintain water filters.",
      "Replace air filters on schedule.",
      "Store products properly.",
      "Reduce duplicate cleaners.",
      "Reduce fragranced laundry products.",
      "Keep personal-care routines simpler and more intentional.",
      "Ventilate during nail care, hair treatments, cleaning, hobby work, or cooking.",
    ],
  },
  {
    key: "D", title: "Defer With a Plan",
    when: [
      "The source is stable.",
      "There is no active moisture or urgent condition.",
      "Replacement would be expensive or disruptive.",
      "A future project creates a better opportunity.",
      "The current product or material is not ideal but is functional, maintained, and not creating a clear problem.",
    ],
    examples: [
      "Keep stable flooring until a planned renovation.",
      "Replace cabinetry during a future kitchen project.",
      "Upgrade kitchen exhaust during appliance or cabinet replacement.",
      "Change insulation during a larger envelope project.",
      "Replace furniture or mattresses during a natural replacement cycle.",
      "Shift cosmetics, cleaning products, or textiles as they run out rather than discarding everything at once.",
    ],
  },
  {
    key: "E", title: "Monitor Only",
    when: [
      "There is no clear source.",
      "There is no clear pathway.",
      "The concern is not repeated.",
      "The condition is low-impact or uncertain.",
      "Testing would not change a decision.",
      "The product or material is stable, low-use, and not close to the body or occupied space.",
    ],
    examples: [
      "One-time odor that resolves and does not repeat.",
      "A low-use item in a low-priority room.",
      "A product already removed from the home.",
      "A stable material with no moisture, odor, dust, or contact concern.",
      "An occasional personal-care or cosmetic product used rarely and not connected to a known concern.",
    ],
  },
];

export const DT_PRIORITY_QUESTIONS = [
  "Is the source or input clearly identified?",
  "Is it repeated, large, close, long-lasting, or used directly on the body?",
  "Is there a clear exposure pathway (air, water, dust, skin, mouth, or food contact)?",
  "Is it near a high-use, sleeping, child, work, kitchen, bathroom, vehicle, or body-contact space?",
  "Is moisture, heat, combustion, spraying, dust, or disturbance involved?",
  "Is it easy to reduce or remove?",
  "Is it connected to a planned purchase, project, or routine change?",
  "Would testing, documentation, or professional evaluation change the decision?",
];

export const DT_DAILY_FILTER = {
  intro:
    "For cosmetics, personal-care products, cleaning products, laundry products, and other recurring inputs, ask:",
  questions: [
    "Do I use this daily or weekly?",
    "Is it leave-on or rinse-off?",
    "Is it sprayed, heated, burned, or diffused?",
    "Is it fragranced?",
    "Is it used on the face, lips, scalp, underarms, hands, or large skin areas?",
    "Is it used by children or during a higher-priority household stage?",
    "Does it duplicate another product?",
    "Do I know why I am using it?",
    "Is there a simpler product that performs the same function?",
    "Can I replace it when it runs out rather than discarding everything now?",
  ],
  higher: ["Repeated", "Leave-on", "Fragranced", "Sprayed", "Heated", "High-contact", "Body-adjacent products"],
  lower: [
    "Occasional", "Rinse-off", "Low-contact", "Well-tolerated",
    "Clearly useful products that are not sprayed, heated, layered, or used in large amounts",
  ],
};

export const DT_ENV_FILTER = {
  intro:
    "For work, school, vehicles, travel, gyms, salons, public buildings, and shared spaces, ask:",
  questions: [
    "How often am I in this environment?",
    "How long do I stay there?",
    "Is there odor, dust, moisture, combustion, renovation, cleaning, fragrance, or poor ventilation?",
    "Is the source temporary or recurring?",
    "Can I change timing, distance, ventilation, filtration, cleaning, or product use?",
    "Is this something to manage personally, ask about, document, or avoid during certain activities?",
    "Would professional evaluation be needed because the concern is occupational, building-wide, or outside household control?",
  ],
  responses: [
    "Increasing distance from the source",
    "Improving ventilation where allowed",
    "Using filtration where practical",
    "Adjusting timing",
    "Reducing product use",
    "Asking facility managers about maintenance or ventilation",
    "Avoiding unnecessary exposure during active renovation, painting, spraying, or high-dust work",
    "Documenting recurring conditions",
  ],
};

export const DT_FINAL_RULE = {
  intro: "Prioritize sources and inputs that are:",
  items: [
    "Repeated",
    "Close to the body",
    "Used in sleeping or high-use spaces",
    "Applied directly to skin, hair, lips, nails, or teeth",
    "Connected to air, water, dust, skin contact, mouth contact, food contact, or breathing-zone pathways",
    "Moisture-related",
    "Heated, burned, sprayed, diffused, or disturbed",
    "Large in surface area",
    "Difficult to clean",
    "Likely to be covered or concealed by future work",
    "Practical to reduce during a natural repair, replacement, restocking, or routine-change cycle",
  ],
  closing:
    "Cumulative exposure is not managed through perfection. It is managed through sequence, source control, simplification, maintenance, and repeated practical decisions across the home, body, workplace, vehicle, and daily life.",
};

// Outcome accent colors (brand palette)
export const DT_OUTCOME_COLOR = {
  A: "#00E599",
  B: "#43C6F0",
  C: "#FFB259",
  D: "#B98CFF",
  E: "#8A9A93",
};

export const DECISION_TREE_PDF_PATH = "/api/download/decision-tree";
